import React, { useState } from 'react';
import { X, Check, Filter, Clock, TrendingUp, TrendingDown, Type, MapPin } from 'lucide-react';
import { formatNaira } from '../../utils/formatters';
import { NIGERIAN_STATES } from '../../constants/nigeria';

const MobileFilterModal = ({ 
  filters, 
  onFilterChange, 
  listingCount,
  isOpen,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState('sort');
  const [priceRange, setPriceRange] = useState({
    min: filters.minPrice || 0,
    max: filters.maxPrice || 1000000
  });

  const sortOptions = [
    { id: 'newest', label: 'Newest First', icon: Clock },
    { id: 'price_low_high', label: 'Price: Low to High', icon: TrendingUp },
    { id: 'price_high_low', label: 'Price: High to Low', icon: TrendingDown },
    { id: 'name_asc', label: 'Name: A to Z', icon: Type },
    { id: 'name_desc', label: 'Name: Z to A', icon: Type },
  ];

  const conditionOptions = [
    { id: 'all', label: 'All Conditions' },
    { id: 'new', label: 'New' },
    { id: 'used', label: 'Used' },
  ];

  const hasActiveFilters = 
    filters.sortBy !== 'newest' || 
    filters.condition !== 'all' || 
    filters.location ||
    filters.minPrice ||
    filters.maxPrice;

  const handlePriceApply = () => {
    onFilterChange({ 
      ...filters, 
      minPrice: priceRange.min,
      maxPrice: priceRange.max
    });
  };

  const clearAllFilters = () => {
    onFilterChange({
      category: null,
      minPrice: null,
      maxPrice: null,
      condition: 'all',
      location: null,
      sortBy: 'newest',
    });
    setPriceRange({ min: 0, max: 1000000 });
  };

  // Don't render if not visible
  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 z-50 transform transition-transform duration-300 ease-in-out">
        <div className="w-full overflow-hidden rounded-t-2xl bg-white">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Filter & Sort
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  {listingCount} listings found
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full hover:bg-gray-100"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'sort', label: 'Sort', icon: Filter },
              { id: 'condition', label: 'Condition', icon: Clock },
              { id: 'location', label: 'Location', icon: MapPin },
              { id: 'price', label: 'Price', icon: TrendingUp }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center py-4 gap-2 transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#ff660e] border-b-2 border-[#ff660e]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="max-h-[60vh] overflow-y-auto">
            {/* Sort Options */}
            {activeTab === 'sort' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sort By</h3>
                <div className="space-y-3">
                  {sortOptions.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.id}
                        onClick={() => {
                          onFilterChange({ ...filters, sortBy: option.id });
                          onClose();
                        }}
                        className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                          filters.sortBy === option.id
                            ? 'border-[#ff660e] bg-[#fff5e6]'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            filters.sortBy === option.id
                              ? 'bg-[#ff660e] bg-opacity-20'
                              : 'bg-gray-100'
                          }`}>
                            <Icon className={`w-5 h-5 ${
                              filters.sortBy === option.id
                                ? 'text-[#ff660e]'
                                : 'text-gray-600'
                            }`} />
                          </div>
                          <span className={`font-medium ${
                            filters.sortBy === option.id
                              ? 'text-[#ff660e]'
                              : 'text-gray-700'
                          }`}>
                            {option.label}
                          </span>
                        </div>
                        {filters.sortBy === option.id && (
                          <Check className="w-5 h-5 text-[#ff660e]" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Condition Filter */}
            {activeTab === 'condition' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Condition</h3>
                <div className="space-y-3">
                  {conditionOptions.map(option => (
                    <button
                      key={option.id}
                      onClick={() => {
                        onFilterChange({ ...filters, condition: option.id });
                        onClose();
                      }}
                      className={`w-full flex items-center justify-between p-4 rounded-xl border transition-colors ${
                        filters.condition === option.id
                          ? 'border-[#ff660e] bg-[#fff5e6]'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          filters.condition === option.id
                            ? 'bg-[#ff660e] bg-opacity-20'
                            : 'bg-gray-100'
                        }`}>
                          <span className="text-lg">
                            {option.id === 'all' ? '🌐' : option.id === 'new' ? '🆕' : '🔄'}
                          </span>
                        </div>
                        <span className={`font-medium ${
                          filters.condition === option.id
                            ? 'text-[#ff660e]'
                            : 'text-gray-700'
                        }`}>
                          {option.label}
                        </span>
                      </div>
                      {filters.condition === option.id && (
                        <Check className="w-5 h-5 text-[#ff660e]" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Location Filter */}
            {activeTab === 'location' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Location</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => {
                      onFilterChange({ ...filters, location: null });
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg border ${
                      !filters.location
                        ? 'border-[#ff660e] bg-[#fff5e6] text-[#ff660e]'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <span className="font-medium">All Nigeria</span>
                    {!filters.location && <Check className="w-5 h-5" />}
                  </button>
                  
                  {NIGERIAN_STATES.slice(0, 15).map(state => (
                    <button
                      key={state.id}
                      onClick={() => {
                        onFilterChange({ ...filters, location: state.id });
                      }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg border ${
                        filters.location === state.id
                          ? 'border-[#ff660e] bg-[#fff5e6] text-[#ff660e]'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <span className="font-medium">{state.name}</span>
                      {filters.location === state.id && <Check className="w-5 h-5" />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Price Filter */}
            {activeTab === 'price' && (
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Price Range</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-700">Min: {formatNaira(priceRange.min)}</div>
                    <div className="text-sm font-medium text-gray-700">Max: {formatNaira(priceRange.max)}</div>
                  </div>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Minimum</label>
                      <input
                        type="range"
                        min={0}
                        max={1000000}
                        step={1000}
                        value={priceRange.min}
                        onChange={(e) => setPriceRange(prev => ({...prev, min: parseInt(e.target.value)}))}
                        className="w-full h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#ff660e] [&::-webkit-slider-thumb]:cursor-pointer"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-600 mb-2">Maximum</label>
                      <input
                        type="range"
                        min={0}
                        max={1000000}
                        step={1000}
                        value={priceRange.max}
                        onChange={(e) => setPriceRange(prev => ({...prev, max: parseInt(e.target.value)}))}
                        className="w-full h-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#ff660e] [&::-webkit-slider-thumb]:cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  <button
                    onClick={handlePriceApply}
                    className="w-full py-3 bg-[#ff660e] text-white rounded-xl font-medium hover:bg-[#e55a0d] transition-colors"
                  >
                    Apply Price Filter
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-gray-200 space-y-3">
            {hasActiveFilters && (
              <button
                onClick={() => {
                  clearAllFilters();
                  onClose();
                }}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-[#ff660e] text-[#ff660e] font-semibold hover:bg-[#fff5e6] transition-colors"
              >
                <X className="w-5 h-5" />
                Clear All Filters
              </button>
            )}
            
            <button
              onClick={onClose}
              className="w-full bg-[#ff660e] text-white font-semibold py-3 rounded-xl hover:bg-[#e55a0d] transition-colors shadow-md"
            >
              Show Results
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default MobileFilterModal;